import React, { createContext, useContext, useEffect, useState } from 'react';
import { User as FirebaseUser } from 'firebase/auth';
import { authService } from '../services/auth.service';
import { notificationService } from '../services/notification.service';
import { AuthContextType, User, AuthUser, LoginCredentials, RegisterData } from '../types/auth.types';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [authUser, setAuthUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = authService.onAuthStateChanged(async (firebaseUser: FirebaseUser | null) => {
      console.log('🔵 AuthContext: onAuthStateChanged 트리거됨', firebaseUser?.uid);
      setLoading(true);
      
      if (firebaseUser) {
        // Firebase Auth 사용자 정보
        const authUserData: AuthUser = {
          uid: firebaseUser.uid,
          email: firebaseUser.email!,
          displayName: firebaseUser.displayName || undefined
        };
        console.log('🟢 AuthContext: authUser 설정됨:', authUserData);
        setAuthUser(authUserData);

        // Firestore에서 추가 사용자 정보 가져오기
        const userData = await authService.getUserData(firebaseUser.uid);
        console.log('🟢 AuthContext: userData 가져옴:', userData);
        setUser(userData);

        // 로그인 시 자동 체크 실행
        // 1. 수명 알림 체크
        notificationService.checkAndSendLifetimeAlerts(firebaseUser.uid).catch(err => {
          console.error('수명 알림 체크 오류:', err);
        });

        // 2. 예약된 리포트 발송 체크
        notificationService.checkAndSendScheduledReports(firebaseUser.uid).catch(err => {
          console.error('리포트 발송 체크 오류:', err);
        });
      } else {
        console.log('🔴 AuthContext: 사용자 로그아웃 상태');
        setAuthUser(null);
        setUser(null);
      }
      
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const login = async (credentials: LoginCredentials): Promise<void> => {
    try {
      console.log('🔵 AuthContext: 로그인 함수 시작');
      const result = await authService.login(credentials);
      console.log('🟢 AuthContext: authService.login 완료:', result);
      // onAuthStateChanged에서 사용자 상태 업데이트
    } catch (error: any) {
      console.error('🔴 AuthContext: 로그인 에러:', error.message);
      throw error; // 원본 Firebase 에러 객체 전달
    }
  };

  const logout = async (): Promise<void> => {
    try {
      setLoading(true);
      await authService.logout();
      // onAuthStateChanged에서 사용자 상태 업데이트
    } catch (error: any) {
      throw new Error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const register = async (data: RegisterData): Promise<void> => {
    try {
      setLoading(true);
      await authService.register(data);
      // onAuthStateChanged에서 사용자 상태 업데이트
    } catch (error: any) {
      throw new Error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const value: AuthContextType = {
    user,
    authUser,
    loading,
    login,
    logout,
    register
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
